import { useEffect, useCallback, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getLeaderboard, getExam } from '@/api/exams'
import { Chip } from '@/components/aurora'
import { Trophy, Radio, Clock } from 'lucide-react'

function fmt(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Guards against floating-point summation artifacts (e.g. 105.59999999999994)
function fmtScore(n) {
  return Math.round((n ?? 0) * 100) / 100
}

/* ── Podium slot ─────────────────────────────────────────── */
function PodiumSlot({ row, rank, isMe }) {
  if (!row) return <div className="flex-1" />

  const configs = {
    1: {
      platformH: 120,
      platformBg: 'linear-gradient(180deg, #FCD34D 0%, #F59E0B 100%)',
      avatarBg: '#FEF3C7',
      avatarBorder: '#FCD34D',
      avatarColor: '#92400E',
    },
    2: {
      platformH: 90,
      platformBg: 'linear-gradient(180deg, #E2E8F0 0%, #CBD5E1 100%)',
      avatarBg: '#F1F5F9',
      avatarBorder: '#CBD5E1',
      avatarColor: '#475569',
    },
    3: {
      platformH: 70,
      platformBg: 'linear-gradient(180deg, #FDBA74 0%, #C2856A 100%)',
      avatarBg: '#FEF3E8',
      avatarBorder: '#FDBA74',
      avatarColor: '#7C2D12',
    },
  }

  const c = configs[rank]
  const avatarSize = rank === 1 ? 64 : 52

  return (
    <div className="flex-1 flex flex-col items-center" style={{ marginTop: rank === 1 ? 0 : rank === 2 ? 30 : 50 }}>
      <div
        className="rounded-full grid place-items-center font-black border-4 mb-1 shadow-md"
        style={{
          width: avatarSize, height: avatarSize,
          background: c.avatarBg,
          borderColor: c.avatarBorder,
          color: c.avatarColor,
          fontSize: rank === 1 ? 24 : 18,
          boxShadow: isMe ? `0 0 0 3px var(--coral)` : undefined,
        }}
      >
        {row.student_name?.[0]?.toUpperCase() ?? '?'}
      </div>

      <p
        className="font-bold text-center m-0 line-clamp-1 px-1"
        style={{ color: 'var(--ink)', fontSize: rank === 1 ? 14 : 12.5, maxWidth: 100 }}
      >
        {row.student_name}
      </p>

      <p className="font-mono m-0 mt-0.5" style={{ color: 'var(--ink-2)', fontSize: 12 }}>
        {fmtScore(row.total_score)} ball
      </p>

      {isMe && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full mt-1" style={{ background: 'var(--coral)', color: '#fff' }}>
          Siz
        </span>
      )}

      <div
        className="w-full mt-3 rounded-t-2xl flex items-center justify-center shadow-inner"
        style={{ height: c.platformH, background: c.platformBg }}
      >
        <span className="font-black text-white/70" style={{ fontSize: rank === 1 ? 52 : 40 }}>{rank}</span>
      </div>
    </div>
  )
}

export default function LeaderboardPage() {
  const { code }  = useParams()
  const id = code
  const { t }     = useTranslation()

  const [exam,      setExam]      = useState(null)
  const [board,     setBoard]     = useState([])
  const [frozenAt,  setFrozenAt]  = useState(null)
  const [isLive,    setIsLive]    = useState(false)
  const [live,      setLive]      = useState(false)
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState(null)

  // Guards against a stale response — from a previous exam `id` (route param
  // changed without unmount) or a StrictMode double-mount in dev — landing
  // after a newer request has already resolved.
  const requestIdRef = useRef(0)

  const fetchBoard = useCallback(async (liveMode, knownExam) => {
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    try {
      const [eRes, bRes] = await Promise.all([
        knownExam ? Promise.resolve({ data: knownExam }) : getExam(id),
        getLeaderboard(id, liveMode),
      ])
      if (reqId !== requestIdRef.current) return
      setExam(eRes.data)
      const payload = bRes.data
      setBoard(payload.entries ?? payload ?? [])
      setFrozenAt(payload.frozen_at ?? null)
      setIsLive(payload.is_live ?? true)
    } catch (e) {
      if (reqId === requestIdRef.current) setError(e.response?.data?.detail || e.message)
    } finally {
      if (reqId === requestIdRef.current) setLoading(false)
    }
  }, [id])

  useEffect(() => {
    // Clear the previous exam's data immediately — otherwise, if `id` changes
    // without an unmount (client-side nav to a different exam's leaderboard),
    // `exam` still holds the old exam and would be reused as-is below.
    setExam(null)
    fetchBoard(false)
  }, [id, fetchBoard])

  function toggleLive() {
    const next = !live
    setLive(next)
    fetchBoard(next, exam)
  }

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--coral)' }}/>
    </div>
  )
  if (error) return (
    <div className="flex items-center justify-center py-24">
      <p className="text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
    </div>
  )

  /* visual order: 2nd | 1st | 3rd */
  const top3 = [board[1], board[0], board[2]]

  return (
    <div className="px-4 py-8">
      <div className="max-w-2xl mx-auto flex flex-col gap-5">

        {/* Title + live toggle */}
        <div className="flex items-start justify-between gap-3">
          <h1 className="m-0 text-xl font-bold flex items-center gap-2" style={{ color: 'var(--ink)' }}>
            <Trophy size={22} strokeWidth={2} style={{ color: '#F59E0B' }}/> {exam?.title}
          </h1>
          {frozenAt && (
            <button
              onClick={toggleLive}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border"
              style={live
                ? { background: 'var(--coral)', color: '#fff', borderColor: 'var(--coral)' }
                : { background: 'var(--surface)', color: 'var(--ink-2)', borderColor: 'var(--border)' }
              }
            >
              <Radio size={13} />
              {live ? 'Jonli reyting' : 'Umumiy reyting'}
            </button>
          )}
        </div>

        {/* Snapshot badge */}
        {frozenAt && !live && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
            style={{ background: 'var(--surface)', color: 'var(--ink-3)', border: '1px solid var(--border)' }}
          >
            <Clock size={13} style={{ flexShrink: 0 }} />
            <span>Asosiy reyting: <strong style={{ color: 'var(--ink)' }}>{fmt(frozenAt)}</strong> holatida</span>
          </div>
        )}

        {/* Podium */}
        {board.length >= 3 && (
          <div
            className="rounded-2xl border overflow-hidden"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
          >
            <p
              className="text-[12px] font-semibold uppercase tracking-widest text-center pt-5 pb-2 m-0"
              style={{ color: 'var(--ink-3)' }}
            >
              Top 3
            </p>

            <div className="flex items-end gap-0 px-4">
              <PodiumSlot row={top3[0]} rank={2} isMe={top3[0]?.my_result} />
              <PodiumSlot row={top3[1]} rank={1} isMe={top3[1]?.my_result} />
              <PodiumSlot row={top3[2]} rank={3} isMe={top3[2]?.my_result} />
            </div>
          </div>
        )}

        {/* Full list */}
        <div
          className="rounded-2xl border px-5 py-5"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <p
            className="text-[12.5px] font-semibold uppercase tracking-wider m-0 mb-3"
            style={{ color: 'var(--ink-3)' }}
          >
            {t('leaderboard.allResults', 'Barcha natijalar')} ({board.length})
          </p>
          <div className="flex flex-col gap-2">
            {board.map((row, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all"
                style={row.my_result
                  ? { background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)' }
                  : { background: 'var(--bg)', borderColor: 'var(--border)' }
                }
              >
                <span
                  className="font-mono text-[12px] w-7 text-center flex-shrink-0"
                  style={{ color: 'var(--ink-3)' }}
                >
                  {i < 3 ? ['🥇','🥈','🥉'][i] : i + 1}
                </span>
                <span className="flex-1 text-[13.5px] font-medium truncate" style={{ color: 'var(--ink)' }}>
                  {row.student_name}
                </span>
                <span className="font-mono text-[12px] flex-shrink-0" style={{ color: 'var(--ink-2)' }}>
                  {fmtScore(row.total_score)}
                </span>
                {row.grade_label && <Chip variant="grade" className="flex-shrink-0">{row.grade_label}</Chip>}
                {row.my_result   && <Chip variant="new"   className="flex-shrink-0">Siz</Chip>}
                {row.finished_at && (
                  <span className="font-mono text-[10px] hidden sm:block flex-shrink-0" style={{ color: 'var(--ink-3)' }}>
                    {fmt(row.finished_at)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}
